import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import type { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Search } from '@rover/icons';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import AddMoreDetailsSection from './AddMoreDetailsSection';
import {
  ColorfulPerfectMatchFieldInteraction,
  ColorfulPerfectMatchSearchTap,
} from './analyticsEvents';
import DateInputs from './DateInputs';
import LocationInput from './LocationInput';
import { getPetTypeFromCounts } from './petCountUtils';
import PetDetailsBottomSheet from './PetDetailsBottomSheet';
import PetTypeSelector from './PetTypeSelector';
import ServiceTypeGrid, { getIsDogOnlyService } from './ServiceTypeGrid';

type PetTypeOption = 'dog' | 'cat';

export type Props = {
  searchFilters: SearchFilters;
  updateFiltersAndFireSearch: (filters: Partial<SearchFilters>) => void;
  fireSearch: (options?: { searchInitiatedByMap: boolean }) => void;
  onSubmit: () => void;
  locale: Locale;
  pets: Pet[];
  isUserAuthenticated: boolean;
  serviceTypeFilterOptions: SearchFiltersOptions;
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  weightUnit: string;
  /**
   * Reports whether this section occupies any of the viewport. The section is
   * hidden by a breakpoint rather than unmounted, so above the mobile
   * breakpoint it has no box and is reported as not visible.
   */
  onVisibilityChange?: (isVisible: boolean) => void;
};

const ColorfulPerfectMatchSection = ({
  searchFilters,
  updateFiltersAndFireSearch,
  fireSearch,
  onSubmit,
  locale,
  pets,
  isUserAuthenticated,
  serviceTypeFilterOptions,
  petSizeBuckets,
  weightUnit,
  onVisibilityChange,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const [selectedPetType, setSelectedPetType] = useState<PetTypeOption | null>(null);
  const [isPetSheetOpen, setIsPetSheetOpen] = useState(false);

  // Must stay on the element carrying the breakpoint `display`: a wrapper is
  // still laid out above the mobile breakpoint and would report itself visible.
  const sectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const element = sectionRef.current;
    if (!element || !onVisibilityChange) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      onVisibilityChange(entry.isIntersecting);
    });
    observer.observe(element);

    // A section that is gone occupies nothing, and saying so keeps a listener
    // that hides itself against this section from staying hidden for good if
    // an error boundary tears the section down mid-session.
    return () => {
      observer.disconnect();
      onVisibilityChange(false);
    };
  }, [onVisibilityChange]);

  const [initialDogBreeds, initialCatBreeds] = useMemo(() => {
    const dogSet = new Set<string>();
    const catSet = new Set<string>();
    pets.forEach((pet) => {
      const isCat = pet.petType === PetType.CAT;
      const target = isCat ? catSet : dogSet;
      (pet.breeds ?? []).forEach((breed) => target.add(breed));
    });
    return [Array.from(dogSet), Array.from(catSet)];
  }, [pets]);

  const [dogBreeds, setDogBreeds] = useState<string[]>(initialDogBreeds);
  const [catBreeds, setCatBreeds] = useState<string[]>(initialCatBreeds);

  const isCatOnly = getIsCatOnlyPetType(searchFilters.petType || []);

  const hasOwnerDog = pets.some((pet) => pet.petType === PetType.DOG);
  const hasOwnerCat = pets.some((pet) => pet.petType === PetType.CAT);
  const hideDogOption = isUserAuthenticated && hasOwnerCat && !hasOwnerDog;

  // A dog-only service (e.g. day care, walking) can't apply to cats, so hide the
  // cat option unless a cat is already part of the search.
  const noCatsSelected = !(searchFilters.petType ?? []).includes(PetType.CAT);
  const dogOnlyServiceSelected = getIsDogOnlyService(searchFilters.serviceType);
  const hideCatOption =
    (isUserAuthenticated && hasOwnerDog && !hasOwnerCat) ||
    (dogOnlyServiceSelected && noCatsSelected);

  const theme = useTheme();
  // Styles for checkboxes in the redesigned expanded section.
  // The checkboxes component is used in multiple places, and introducing styling props
  // would add unnecessary complexity. Applying styles here keeps the change isolated.
  const sectionOverrides = {
    '& label:has(input:checked)': {
      boxShadow: `inset 0 0 0 2px ${theme.colors.interactive_link.primary_base}`,
      backgroundColor: `${theme.colors.interactive_link.primary_inverse_pressed} !important`,
    },
    // Match FilterLabel font-size to Kibble Text size="200"
    [`& ${FilterLabel}`]: {
      fontSize: theme.text['200'].fontSize,
    },
  };

  const handlePetTypeSelect = useCallback(
    (petType: PetTypeOption) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'pet_type' }));

      // Also increment the pet counter when first selecting a pet type
      const dogCount = searchFilters.dogCount || 0;
      const catCount = searchFilters.catCount || 0;
      const puppyCount = searchFilters.puppyCount || 0;
      if (petType === 'dog' && dogCount + puppyCount === 0) {
        updateFiltersAndFireSearch({
          dogCount: 1,
          petType: getPetTypeFromCounts(1, catCount, puppyCount),
        });
      } else if (petType === 'cat' && catCount === 0) {
        updateFiltersAndFireSearch({
          catCount: 1,
          petType: getPetTypeFromCounts(dogCount, 1, puppyCount),
        });
      }

      setSelectedPetType(petType);
      setIsPetSheetOpen(true);
    },
    [searchFilters, updateFiltersAndFireSearch]
  );

  const handleServiceSelect = useCallback(
    (serviceType: ServiceType) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'service' }));
      updateFiltersAndFireSearch({ serviceType });
    },
    [updateFiltersAndFireSearch]
  );

  const handleSearch = useCallback(() => {
    emitAnalyticsEvent(new ColorfulPerfectMatchSearchTap());
    fireSearch({ searchInitiatedByMap: false });
    onSubmit();
  }, [fireSearch, onSubmit]);

  const handleBottomsheetClose = useCallback(
    (saved: boolean) => {
      if (saved) fireSearch({ searchInitiatedByMap: false });
      setIsPetSheetOpen(false);
    },
    [fireSearch]
  );

  return (
    <Box ref={sectionRef} display={['block', 'none']} p="4x" sx={sectionOverrides}>
      <Flex gap="2x" alignItems="center" mb="6x">
        <Search width={24} height={24} />
        <Heading size="300" mb="0x">{t`Find the perfect match`}</Heading>
      </Flex>

      <Flex flexDirection="column" gap="6x">
        <PetTypeSelector
          onSelect={handlePetTypeSelect}
          dogCount={(searchFilters.dogCount || 0) + (searchFilters.puppyCount || 0)}
          catCount={searchFilters.catCount || 0}
          hideDog={hideDogOption}
          hideCat={hideCatOption}
        />

        <ServiceTypeGrid
          selectedService={searchFilters.serviceType}
          onSelect={handleServiceSelect}
          petType={searchFilters.petType}
        />

        <LocationInput location={searchFilters.location} onChange={updateFiltersAndFireSearch} />

        <DateInputs
          searchFilters={searchFilters}
          locale={locale}
          serviceType={searchFilters.serviceType}
          serviceTypeFilterOptions={serviceTypeFilterOptions}
          onChange={updateFiltersAndFireSearch}
        />

        <AddMoreDetailsSection
          searchFilters={searchFilters}
          onChange={updateFiltersAndFireSearch}
          serviceTypeFilterOptions={serviceTypeFilterOptions}
          petSizeBuckets={petSizeBuckets}
          weightUnit={weightUnit}
          isCatOnly={isCatOnly}
        />
      </Flex>

      <Box mt="6x">
        <Button variant="primary" fullWidth onPress={handleSearch}>
          {t`Search`}
        </Button>
      </Box>

      {selectedPetType && (
        <PetDetailsBottomSheet
          isOpen={isPetSheetOpen}
          onClose={handleBottomsheetClose}
          petType={selectedPetType}
          searchFilters={searchFilters}
          pets={isUserAuthenticated ? pets : []}
          dogCount={searchFilters.dogCount || 0}
          catCount={searchFilters.catCount || 0}
          puppyCount={searchFilters.puppyCount || 0}
          onChange={updateFiltersAndFireSearch}
          breeds={selectedPetType === 'dog' ? dogBreeds : catBreeds}
          onBreedsChange={selectedPetType === 'dog' ? setDogBreeds : setCatBreeds}
        />
      )}
    </Box>
  );
};

export default ColorfulPerfectMatchSection;
